/**
 * LogisticsBooking — dedicated booking page for courier / same-day / business
 * logistics vendors. Presents the service card, pickup & delivery inputs,
 * parcel-size picker, priority toggle, live quote, and a legally-worded
 * contents-declaration agreement that the customer types their name into.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Bike, Truck, Package, MapPin, ShieldCheck, Loader2,
  Zap, AlertTriangle, ChevronRight, Star, Sparkles,
} from 'lucide-react';
import api, { catalogApi, ordersApi } from '../services/api';
import Header from '../components/Header';
import AddressAutocomplete from '../components/AddressAutocomplete';
import ShippingSizePicker from '../components/ShippingSizePicker';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { toast } from 'sonner';

const CATEGORY_LABELS = {
  courier: { label: 'Courier delivery', icon: Bike, tint: 'bg-emerald-100 text-emerald-700' },
  package: { label: 'Same-day package', icon: Package, tint: 'bg-sky-100 text-sky-700' },
  logistics: { label: 'Business logistics', icon: Truck, tint: 'bg-amber-100 text-amber-700' },
};

const LogisticsBooking = () => {
  const { vendorId } = useParams();
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { format } = useApp();

  const vendor_prefill = useMemo(() => {
    const numOrNull = (v) => { const n = Number(v); return Number.isFinite(n) ? n : null; };
    return {
      pickup: searchParams.get('pickup') || '',
      pickup_lat: numOrNull(searchParams.get('pickup_lat')),
      pickup_lng: numOrNull(searchParams.get('pickup_lng')),
      drop: searchParams.get('drop') || '',
      drop_lat: numOrNull(searchParams.get('drop_lat')),
      drop_lng: numOrNull(searchParams.get('drop_lng')),
      priority: searchParams.get('priority') === '1',
    };
  }, []); // capture once on mount

  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shippingCfg, setShippingCfg] = useState(null);
  const [catCfg, setCatCfg] = useState(null);

  const [pickupAddress, setPickupAddress] = useState(vendor_prefill.pickup);
  const [pickupLat, setPickupLat] = useState(vendor_prefill.pickup_lat);
  const [pickupLng, setPickupLng] = useState(vendor_prefill.pickup_lng);
  const [dropAddress, setDropAddress] = useState(vendor_prefill.drop);
  const [dropLat, setDropLat] = useState(vendor_prefill.drop_lat);
  const [dropLng, setDropLng] = useState(vendor_prefill.drop_lng);
  const [sizeId, setSizeId] = useState(null);
  const [priority, setPriority] = useState(vendor_prefill.priority);
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);

  const [contents, setContents] = useState('');
  const [agree, setAgree] = useState(false);
  const [signature, setSignature] = useState('');
  const [placing, setPlacing] = useState(false);
  const [localDrivers, setLocalDrivers] = useState([]);
  const [driversLoading, setDriversLoading] = useState(false);
  const [preferredDriverId, setPreferredDriverId] = useState(null);
  const [driverRadiusKm, setDriverRadiusKm] = useState(15);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [v, s, c] = await Promise.all([
          catalogApi.vendor(vendorId),
          api.get('/shipping-sizes').then((r) => r.data),
          api.get('/category-pricing').then((r) => r.data),
        ]);
        if (!alive) return;
        setVendor(v);
        setShippingCfg(s);
        setCatCfg(c);
      } catch {
        toast.error('Could not load the service.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [vendorId]);

  const catMeta = CATEGORY_LABELS[String(vendor?.category || '').toLowerCase()]
    || { label: vendor?.category || 'Logistics', icon: Truck, tint: 'bg-slate-100 text-slate-700' };
  const CatIcon = catMeta.icon;

  const canQuote = pickupLat != null && pickupLng != null && dropLat != null && dropLng != null;

  const fetchQuote = async () => {
    if (!canQuote) return;
    setQuoting(true);
    try {
      const { data } = await api.post('/orders/quote', {
        vendor_id: vendorId,
        pickup_lat: pickupLat,
        pickup_lng: pickupLng,
        pickup_address: pickupAddress || undefined,
        dest_lat: dropLat,
        dest_lng: dropLng,
        shipping_size_id: sizeId || undefined,
        priority,
      });
      setQuote(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not compute a quote');
    } finally {
      setQuoting(false);
    }
  };

  // Auto-refresh the quote when any pricing input changes and both endpoints are set.
  useEffect(() => {
    if (canQuote) fetchQuote();
    else setQuote(null);
  }, [pickupLat, pickupLng, dropLat, dropLng, sizeId, priority]);

  // Fetch local drivers whenever pickup coordinates lock — sorted by best-rated
  // then nearest. When the customer has no lat/lng yet, list stays empty.
  useEffect(() => {
    if (pickupLat == null || pickupLng == null) {
      setLocalDrivers([]);
      return;
    }
    let alive = true;
    setDriversLoading(true);
    api.get('/orders/drivers/local', { params: { pickup_lat: pickupLat, pickup_lng: pickupLng, limit: 12 } })
      .then(({ data }) => {
        if (!alive) return;
        setLocalDrivers(data?.drivers || []);
        if (data?.radius_km) setDriverRadiusKm(data.radius_km);
      })
      .catch(() => { if (alive) setLocalDrivers([]); })
      .finally(() => { if (alive) setDriversLoading(false); });
    return () => { alive = false; };
  }, [pickupLat, pickupLng]);

  const declarationOk = contents.trim().length >= 3 && agree && signature.trim().length >= 2;

  const book = async () => {
    if (!canQuote) { toast.error('Add pickup and delivery addresses first.'); return; }
    if (!declarationOk) { toast.error('Complete the contents declaration and signature.'); return; }
    setPlacing(true);
    try {
      const order = await ordersApi.create({
        vendor_id: vendorId,
        items: [{
          id: 'logistics-shipment',
          name: `${catMeta.label} shipment`,
          price: 0, qty: 1,
        }],
        address: dropAddress || '(from map)',
        dest_lat: dropLat,
        dest_lng: dropLng,
        currency: vendor.currency || 'USD',
        fulfillment_type: 'delivery',
        pickup_lat: pickupLat,
        pickup_lng: pickupLng,
        pickup_address: pickupAddress || undefined,
        shipping_size_id: sizeId || undefined,
        priority,
        preferred_driver_id: preferredDriverId || undefined,
        contents_declaration: contents.trim(),
        signed_agreement: true,
        signature_name: signature.trim(),
      });
      toast.success('Shipment booked!');
      nav(`/track/${order.order_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Booking failed');
    } finally {
      setPlacing(false);
    }
  };

  const feeText = quote ? format(quote.delivery_fee, quote.currency) : '—';

  const sizes = useMemo(() => shippingCfg?.sizes || [], [shippingCfg]);
  const baseFee = useMemo(() => {
    if (!quote || !quote.shipping_size_multiplier || quote.shipping_size_multiplier === 0) return null;
    return Number(quote.delivery_fee) / Number(quote.shipping_size_multiplier);
  }, [quote]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-3xl mx-auto py-24 flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
        </div>
      </div>
    );
  }
  if (!vendor) return null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="logistics-booking">
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <button
          onClick={() => nav(-1)}
          className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
          data-testid="booking-back"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        {/* Service header */}
        <section className="mt-3 bg-white rounded-2xl border border-slate-200 p-5 flex items-center gap-4">
          <div className={`h-12 w-12 rounded-xl ${catMeta.tint} flex items-center justify-center`}>
            <CatIcon className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-wide text-slate-500">{catMeta.label}</div>
            <div className="text-lg font-semibold text-slate-900 truncate" data-testid="booking-vendor-name">
              {vendor.name}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              ⭐ {(vendor.rating || 5).toFixed(1)} · {vendor.currency || 'USD'} pricing
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-slate-400">Estimated fee</div>
            <div className="text-2xl font-semibold text-slate-900 font-mono" data-testid="booking-fee">{feeText}</div>
            {quote && (
              <div className="text-[11px] text-slate-500 mt-0.5">
                {quote.distance_km} km{quote.priority ? ' · priority' : ''}
              </div>
            )}
          </div>
        </section>

        {/* Addresses */}
        <section className="mt-4 bg-white rounded-2xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="h-5 w-5 text-emerald-600" />
            <h2 className="font-semibold text-slate-900">Pickup & delivery</h2>
          </div>
          <label className="text-xs text-slate-500">Pickup address</label>
          <AddressAutocomplete
            value={pickupAddress}
            onChange={(addr, geo) => {
              setPickupAddress(addr || '');
              if (geo?.lat != null && geo?.lng != null) {
                setPickupLat(geo.lat);
                setPickupLng(geo.lng);
              } else if (!addr) {
                setPickupLat(null); setPickupLng(null);
              }
              // Typing keystrokes: preserve any already-locked coords.
            }}
            placeholder="Where should we collect from?"
            data-testid="booking-pickup-input"
          />
          <div className="text-[11px] mt-1" data-testid="booking-pickup-coords">
            {pickupLat != null && pickupLng != null ? (
              <span className="text-emerald-600">✓ Address locked</span>
            ) : (
              <span className="text-amber-600">Pick a suggestion to lock coordinates</span>
            )}
          </div>
          <label className="text-xs text-slate-500 mt-4 block">Delivery address</label>
          <AddressAutocomplete
            value={dropAddress}
            onChange={(addr, geo) => {
              setDropAddress(addr || '');
              if (geo?.lat != null && geo?.lng != null) {
                setDropLat(geo.lat);
                setDropLng(geo.lng);
              } else if (!addr) {
                setDropLat(null); setDropLng(null);
              }
            }}
            placeholder="Where should we deliver to?"
            data-testid="booking-drop-input"
          />
          <div className="text-[11px] mt-1" data-testid="booking-drop-coords">
            {dropLat != null && dropLng != null ? (
              <span className="text-emerald-600">✓ Address locked</span>
            ) : (
              <span className="text-amber-600">Pick a suggestion to lock coordinates</span>
            )}
          </div>
        </section>

        {/* Size + priority */}
        {sizes.length > 0 && (
          <ShippingSizePicker
            sizes={sizes}
            value={sizeId}
            onChange={setSizeId}
            baseFee={baseFee}
            formatMoney={format}
            loading={false}
          />
        )}
        <section className="mt-4 bg-white rounded-2xl border border-slate-200 p-4 flex items-center justify-between gap-3 flex-wrap">
          <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="booking-priority-toggle">
            <input type="checkbox" checked={priority} onChange={(e) => setPriority(e.target.checked)}
                   className="h-4 w-4 accent-emerald-600" />
            <Zap className="h-4 w-4 text-amber-500" />
            <span className="text-sm text-slate-700 font-medium">
              Priority delivery
              {catCfg?.priority_delivery_multiplier > 1 && (
                <span className="text-slate-400 ml-1">(×{Number(catCfg.priority_delivery_multiplier).toFixed(1)})</span>
              )}
            </span>
          </label>
          <div className="text-xs text-slate-500 inline-flex items-center gap-1">
            {quoting ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
            {quote ? <>Distance: <b className="font-mono">{quote.distance_km} km</b></> : 'Quote updates automatically'}
          </div>
        </section>

        {/* Driver picker — nearest & best rated */}
        <section className="mt-4 bg-white rounded-2xl border border-slate-200 p-5" data-testid="booking-driver-picker">
          <div className="flex items-center gap-2 mb-3">
            <Star className="h-5 w-5 text-amber-500" />
            <h2 className="font-semibold text-slate-900">Choose your driver</h2>
            <span className="ml-auto text-[11px] text-slate-400">Within {driverRadiusKm} km · nearest & best rated first</span>
          </div>

          {pickupLat == null ? (
            <div className="text-sm text-slate-500" data-testid="driver-need-pickup">
              Enter a pickup address to see nearby drivers.
            </div>
          ) : driversLoading ? (
            <div className="text-sm text-slate-500 inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Finding drivers near you…
            </div>
          ) : localDrivers.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-600" data-testid="driver-empty">
              <div className="font-medium text-slate-800 mb-0.5">No drivers found in your area.</div>
              <div className="text-xs text-slate-500">
                We&apos;ll open the booking to the marketplace so any available driver within {driverRadiusKm} km can accept it.
                You can still complete this booking below.
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setPreferredDriverId(null)}
                data-testid="driver-auto"
                className={`w-full text-left p-3 rounded-xl border transition-colors ${
                  preferredDriverId == null ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-100' : 'border-slate-200 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                    <Sparkles className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900">Auto-assign nearest driver</div>
                    <div className="text-[11px] text-slate-500">Best pickup ETA — recommended</div>
                  </div>
                  {preferredDriverId == null && <span className="text-[11px] font-semibold text-emerald-700">Selected</span>}
                </div>
              </button>
              {localDrivers.map((d) => {
                const active = preferredDriverId === d.driver_id;
                return (
                  <button
                    key={d.driver_id}
                    type="button"
                    onClick={() => setPreferredDriverId(d.driver_id)}
                    data-testid={`driver-option-${d.driver_id}`}
                    className={`w-full text-left p-3 rounded-xl border transition-colors ${
                      active ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-100' : 'border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-slate-100 overflow-hidden shrink-0">
                        {d.photo ? (
                          <img src={d.photo} alt={d.name} className="h-full w-full object-cover" />
                        ) : (
                          <div className="h-full w-full flex items-center justify-center text-xs font-semibold text-slate-500">
                            {(d.name || '?').slice(0, 1)}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <div className="text-sm font-semibold text-slate-900 truncate">{d.name}</div>
                          {d.is_favorite && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800">Favorite</span>}
                        </div>
                        <div className="text-[11px] text-slate-500 flex items-center gap-2 mt-0.5">
                          <span className="inline-flex items-center gap-0.5">
                            <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                            {d.rating ? d.rating.toFixed(1) : '—'}
                          </span>
                          <span>· {d.distance_km} km away</span>
                          {d.vehicle && <span className="truncate">· {d.vehicle}</span>}
                        </div>
                      </div>
                      {active && <span className="text-[11px] font-semibold text-emerald-700">Selected</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {/* Declaration + signature */}
        <section className="mt-4 bg-white rounded-2xl border border-amber-200 p-5" data-testid="booking-declaration">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="h-5 w-5 text-amber-600" />
            <h2 className="font-semibold text-slate-900">Contents declaration & agreement</h2>
          </div>
          <p className="text-sm text-slate-500 mb-3">
            You&apos;re legally responsible for the package&apos;s contents. Please declare what&apos;s inside so
            drivers and customs (if applicable) know what they&apos;re carrying.
          </p>

          <label className="text-xs text-slate-500">What&apos;s in the package?</label>
          <Textarea
            value={contents}
            onChange={(e) => setContents(e.target.value)}
            placeholder="e.g. 1 laptop, 2 books, gift box"
            rows={3}
            className="mt-1"
            data-testid="booking-contents-input"
          />

          <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
            <div className="font-semibold flex items-center gap-1 mb-1">
              <AlertTriangle className="h-4 w-4" /> Prohibited items
            </div>
            No cash, weapons, illegal drugs, hazardous chemicals, or perishable food without proper packaging.
            ShipitNow may refuse or cancel any shipment at its discretion.
          </div>

          <label className="mt-4 flex items-start gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={agree}
              onChange={(e) => setAgree(e.target.checked)}
              className="h-4 w-4 accent-emerald-600 mt-1"
              data-testid="booking-agree-checkbox"
            />
            <span className="text-sm text-slate-700 leading-snug">
              I confirm the contents above are accurate, do not include any prohibited items, and I accept full
              responsibility for the package and any losses arising from mis-declaration.
            </span>
          </label>

          <label className="text-xs text-slate-500 mt-4 block">Your full legal name (e-signature)</label>
          <Input
            value={signature}
            onChange={(e) => setSignature(e.target.value)}
            placeholder={user?.name || 'Jane Doe'}
            className="mt-1 font-medium"
            data-testid="booking-signature-input"
          />
          <div className="text-[11px] text-slate-400 mt-1">
            Signing electronically has the same legal effect as a handwritten signature.
          </div>
        </section>

        <Button
          onClick={book}
          disabled={!canQuote || !declarationOk || placing}
          className="mt-5 w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white text-base"
          data-testid="booking-submit"
        >
          {placing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
          Book delivery {feeText !== '—' ? `· ${feeText}` : ''}
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
};

export default LogisticsBooking;
